import { SortDirection } from '@warebee/shared/data-access-api-dto';
import { InferResult, sql } from 'kysely';
import _ from 'lodash';
import {
  ActualityHqDataColumn,
  ActualityHqQueryBuilderParams,
  getActualityHqQueryBuilder,
} from './actualityHqQueryBuilder';

const getActualityAggregatedHqSub = (
  params: ActualityAggregatedHqQueryBuilderParams,
) => getActualityAggregatedHqQueryBuilder(params).selectFrom('ahq').selectAll();

export type ActualityAggregatedHqDataRows = InferResult<
  ReturnType<typeof getActualityAggregatedHqSub>
>;
export type ActualityAggregatedHqDataRow =
  ActualityAggregatedHqDataRows[number];

export type ActualityAggregatedHqDataColumn =
  keyof ActualityAggregatedHqDataRow;

export type ActualityAggregatedHqStatsDataRow = Pick<
  ActualityAggregatedHqDataRow,
  | 'eventsCount'
  | 'uomCount'
  | 'uniqLocationsCount'
  | 'visitsCount'
  | 'shiftsCount'
  // | 'minEventTime'
  // | 'maxEventTime'
  | 'totalEmployeeActivityDuration'
  | 'totalIdleDurationShiftStart'
  | 'totalIdleDurationShiftEnd'
  | 'totalIdleDurationBetweenJobs'
  | 'totalWeight'
  | 'totalVolume'
  | 'jobsCount'
  | 'jobGroupsCount'
  | 'analyzedCost'
  | 'analyzedDuration'
  | 'analyzedDistance'
  | 'analyzedDurationTravellingStart'
  | 'analyzedDistanceTravellingStart'
  | 'analyzedCostTravellingStart'
  | 'analyzedDurationTravellingEnd'
  | 'analyzedDistanceTravellingEnd'
  | 'analyzedCostTravellingEnd'
  | 'analyzedDurationTravellingInternal'
  | 'analyzedDistanceTravellingInternal'
  | 'analyzedCostTravellingInternal'
  | 'analyzedDurationTravellingVertical'
  | 'analyzedCostTravellingVertical'
  | 'analyzedDistanceTravellingVertical'
  // | 'analyzedVolume'
  // | 'analyzedWeight'
  | 'analyzedDurationHandling'
  | 'analyzedCostHandling'
  | 'analyzedDurationPicking'
  | 'analyzedCostPicking'
  | 'analyzedDurationReordering'
  | 'analyzedCostReordering'
  | 'uniqueAgentsCount'
  | 'uniqueUsersCount'
  // | 'breaks'
  // | 'meetings'
  // | 'training'
  // | 'recharge'
  // | 'maintenance'
  // | 'misc'

  // | 'analyzedRoute'
  // | 'analyzedReasons'
  // | 'analyzedIgnoredEventsCount'
>;

export type ActualityAggregatedHqStatsDataColumn =
  keyof ActualityAggregatedHqStatsDataRow;

export type ActualityAggregatedHqQueryBuilderParams =
  ActualityHqQueryBuilderParams & {
    groupBy: ActualityHqDataColumn[];
  };

export type ActualityAggregatedHqSortBy = {
  direction?: SortDirection;
  field: ActualityAggregatedHqDataColumn;
};
export function getActualityAggregatedHqQueryBuilder(
  params: ActualityAggregatedHqQueryBuilderParams,
) {
  const informationalEventTypes = params.activity.informationalEventTypes;

  const qb = getActualityHqQueryBuilder(params).with(
    cte => cte('ahq'),
    db =>
      db
        .selectFrom('hq')
        .select(({ fn, ref, cast }) => [
          ...params.groupBy,
          fn
            .agg<string[]>('array_agg', [cast(ref('agentType'), 'json')])
            .distinct()
            .as('agentTypes'),
          fn
            .agg<string[]>('array_agg', [cast(ref('eventProcessType'), 'json')])
            .distinct()
            .as('processTypes'),
          fn.count<number>('eventId').as('eventsCount'),
          fn.sum<number>('quantity').as('uomCount'),
          fn.count<number>('locationId').distinct().as('uniqLocationsCount'),
          fn.count<number>('visitKey').distinct().as('visitsCount'),

          fn.min('eventEndTime').as('minEventTime'),
          fn.max('eventEndTime').as('maxEventTime'),

          fn
            .sum<number>('idleDurationShiftStart')
            .as('totalIdleDurationShiftStart'),
          fn
            .sum<number>('idleDurationShiftEnd')
            .as('totalIdleDurationShiftEnd'),
          fn
            .sum<number>('idleDurationBetweenJobs')
            .as('totalIdleDurationBetweenJobs'),

          sql<number>`sum(${ref('employeeActivityDuration')})`.as(
            'totalEmployeeActivityDuration',
          ),

          fn.sum<number>('handledWeight').as('totalWeight'),
          fn.sum<number>('handledVolume').as('totalVolume'),

          fn.count<number>('jobId').distinct().as('jobsCount'),
          fn.count<number>('jobGroupId').distinct().as('jobGroupsCount'),
          fn.count<number>('shiftId').distinct().as('shiftsCount'),
          // analyzed
          fn.sum<number>('cost').as('analyzedCost'),
          fn.sum<number>('duration').as('analyzedDuration'),
          fn.sum<number>('distance').as('analyzedDistance'),
          fn
            .sum<number>('durationTravellingStart')
            .as('analyzedDurationTravellingStart'),
          fn
            .sum<number>('distanceTravellingStart')
            .as('analyzedDistanceTravellingStart'),
          fn
            .sum<number>('costTravellingStart')
            .as('analyzedCostTravellingStart'),
          fn
            .sum<number>('durationTravellingEnd')
            .as('analyzedDurationTravellingEnd'),
          fn
            .sum<number>('distanceTravellingEnd')
            .as('analyzedDistanceTravellingEnd'),
          fn.sum<number>('costTravellingEnd').as('analyzedCostTravellingEnd'),
          fn
            .sum<number>('durationTravellingInternal')
            .as('analyzedDurationTravellingInternal'),
          fn
            .sum<number>('distanceTravellingInternal')
            .as('analyzedDistanceTravellingInternal'),
          fn
            .sum<number>('costTravellingInternal')
            .as('analyzedCostTravellingInternal'),
          fn
            .sum<number>('durationTravellingVertical')
            .as('analyzedDurationTravellingVertical'),
          fn
            .sum<number>('costTravellingVertical')
            .as('analyzedCostTravellingVertical'),
          fn
            .sum<number>('distanceTravellingVertical')
            .as('analyzedDistanceTravellingVertical'),
          // fn.sum<number>('volume').as('analyzedVolume'),
          // fn.sum<number>('weight').as('analyzedWeight'),
          fn.sum<number>('durationHandling').as('analyzedDurationHandling'),
          fn.sum<number>('costHandling').as('analyzedCostHandling'),
          fn.sum<number>('durationPicking').as('analyzedDurationPicking'),
          fn.sum<number>('costPicking').as('analyzedCostPicking'),
          fn.sum<number>('durationReordering').as('analyzedDurationReordering'),
          fn.sum<number>('costReordering').as('analyzedCostReordering'),
          // fn.sum<number>('route').as('analyzedRoute'),
          // fn.sum<number>('reasons').as('analyzedReasons'),
          // fn.sum<number>('ignoredEventsCount').as('analyzedIgnoredEventsCount'),
          fn.count<number>('agentId').distinct().as('uniqueAgentsCount'),
          fn.count<number>('agentUser').distinct().as('uniqueUsersCount'),
        ])
        .where('eventId', 'is not', null)
        .where('eventType', 'not in', informationalEventTypes)
        .$if(!_.isEmpty(params.groupBy), qb => qb.groupBy(params.groupBy)),
  );
  return qb;
}
